import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import AddAgentModal from '../child/AddAgentModal';

function SidebarComponent({ assistants = [], activeAssistantId, onSelectAssistant, handleSetActiveTab }) {
  const navigate = useNavigate();

  const itemsPerPage = 11;
  const [currentPage, setCurrentPage] = useState(1);
  const [showAddAgentModal, setShowAddAgentModal] = useState(false);

  const totalPages = Math.max(1, Math.ceil(assistants.length / itemsPerPage));
  const currentSafePage = Math.min(currentPage, totalPages);
  const startIndex = (currentSafePage - 1) * itemsPerPage;
  const paginatedAssistants = assistants.slice(startIndex, startIndex + itemsPerPage);

  useEffect(() => {
    if (assistants.length === 0) return;

    const indexOfActive = assistants.findIndex(a => a.id === activeAssistantId);

    if (indexOfActive === -1) {
      const defaultId = assistants[0].id;
      onSelectAssistant?.(defaultId);
      setCurrentPage(1);
      navigate(`/assistant/${defaultId}`, { replace: true });
    } else {
      const pageOfActive = Math.floor(indexOfActive / itemsPerPage) + 1;
      setCurrentPage(pageOfActive);
    }
  }, [assistants, activeAssistantId, onSelectAssistant, navigate]);

  const handlePrev = () => setCurrentPage((prev) => Math.max(1, prev - 1));
  const handleNext = () => setCurrentPage((prev) => Math.min(totalPages, prev + 1));

  const handleAgentClick = (agentId) => {
    onSelectAssistant(agentId);
    navigate(`/assistant/${agentId}`, { replace: true });
    handleSetActiveTab?.();
  };

  return (
    <aside
      className="agent-sidebar"
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '82vh',
        borderRight: '1px solid #e5e7eb',
      }}
    >
      <div className="agent-sidebar-header">
        <div className="agent-sidebar-actions" style={{ width: '100%' }}>
          <button
            type="button"
            className="btn btn-primary-600 radius-8 py-8 d-flex align-items-center gap-2"
            style={{ flexGrow: 1, justifyContent: 'center' }}
            onClick={() => setShowAddAgentModal(true)}
          >
            Create Assistant
          </button>
        </div>
      </div>

      <ul className="agent-list" style={{ flex: 1, overflowY: 'auto' }}>
        {paginatedAssistants.length === 0 ? (
          <li className="agent-list-item text-muted" style={{ padding: '1rem', textAlign: 'center' }}>
            No agents available.
          </li>
        ) : (
          paginatedAssistants.map((agent) => (
            <li
              key={agent.id}
              role="button"
              tabIndex={0}
              className={`agent-list-item ${agent.id === activeAssistantId ? 'selected' : ''}`}
              onClick={() => handleAgentClick(agent.id)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleAgentClick(agent.id)}
            >
              <span className="agent-list-item-name">
                {agent.name.length > 23 ? `${agent.name.slice(0, 24)}...` : agent.name}
              </span>
              <span className={`agent-status ${agent.status === 'active' ? 'agent-status-active' : ''}`}>
                {agent.status}
              </span>
            </li>
          ))
        )}
      </ul>

      {totalPages > 1 && (
        <div
          className="flex justify-between px-4 gap-10"
          style={{
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            paddingTop: '8px',
            paddingBottom: '8px',
            alignItems: 'center',
            borderTop: '1px solid #e5e7eb',
          }}
        >
          <button className="btn btn-sm agent-btn-outline" onClick={handlePrev} disabled={currentSafePage === 1} style={{ flex: 1 }}>
            Prev
          </button>
          <span className="text-xs" style={{ width: '60px', textAlign: 'center' }}>
            {currentSafePage} of {totalPages}
          </span>
          <button className="btn btn-sm agent-btn-outline" onClick={handleNext} disabled={currentSafePage === totalPages} style={{ flex: 1 }}>
            Next
          </button>
        </div>
      )}

      {showAddAgentModal && <AddAgentModal handleCancel={() => setShowAddAgentModal(false)} />}
    </aside>
  );
}

export default SidebarComponent;
